import { ErrorHandler, Inject, Injectable } from '@angular/core';
import { SingletonServiceGuard } from '@lsq-frontend/angular-shared/features/generic-get-services';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import {
    ListAllBusinessUnitNamesAndIdsRequest,
    ListAllBusinessUnitNamesAndIdsService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/buyer-account/account-summary/list-all-business-unit-names-and-ids';
import {
    ListBuyersRequest,
    ListBuyersService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/supplier/relationship/list-buyers';
import {
    AlternateUserKeyType,
    DashboardClaim,
    User
} from '@lsq-frontend/remote-services/enterprise/common';
import {
    GetLaunchDarklyFeatureFlagsService,
    LaunchDarklyFeatureFlagsRequest
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import * as LaunchDarkly from 'launchdarkly-js-client-sdk';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { FeatureFlags } from '../../models/common';
import { ClaimService } from '../claim/claim.service';

export interface CurrentBuyer {
    id?: string;
    companyName?: string;
}

/**
 * Service for managing feature flags.
 */
@Injectable({ providedIn: 'root' })
export class FeatureFlagService {
    private _featureFlagSubject = new ReplaySubject<FeatureFlags>(1);
    public featureFlagStream: Observable<FeatureFlags>;
    private _featureFlagsInitializedSubject = new BehaviorSubject<boolean>(false);
    public featureFlagsInitialized$ = this._featureFlagsInitializedSubject.asObservable();
    public featureFlags: FeatureFlags;
    public ldClient: LaunchDarkly.LDClient;
    public user: User;
    private _ldClientOnReady: () => void;
    private _ldClientOnChange: () => void;

    constructor(
        @Inject(SETTINGS) private settings: ISettings,
        private _userService: UserService,
        private _claimService: ClaimService,
        private _listBuyersService: ListBuyersService,
        private _errorHandler: ErrorHandler,
        private _getLaunchDarklyFeatureFlagsService: GetLaunchDarklyFeatureFlagsService,
        private _listAllBusinessUnitNamesAndIdsService: ListAllBusinessUnitNamesAndIdsService
    ) {
        SingletonServiceGuard.register('FeatureFlagService');
        this.featureFlagStream = this._featureFlagSubject.asObservable();

        const user$ = this._userService.user$.pipe(
            distinctUntilChanged(
                (prev, curr) =>
                    prev?.userKey === curr?.userKey &&
                    prev?.impersonationKey === curr?.impersonationKey &&
                    prev?.currentCompany?.alternateCompanyKey ===
                        curr?.currentCompany?.alternateCompanyKey
            )
        );

        user$
            .pipe(
                switchMap(async (user) => {
                    this._featureFlagsInitializedSubject.next(false);
                    return [user, await this._getCurrentBuyer$(user)];
                })
            )
            .subscribe(async ([user, currentBuyer]: [User, CurrentBuyer]) => {
                if (!user) {
                    this._featureFlagSubject.next({});
                    this._featureFlagsInitializedSubject.next(true);
                    return;
                }
                this.user = user;
                if (this.ldClient) {
                    if (this._ldClientOnReady) {
                        this.ldClient.off('ready', this._ldClientOnReady);
                    }
                    if (this._ldClientOnChange) {
                        this.ldClient.off('change', this._ldClientOnChange);
                    }
                }
                this.ldClient = this.initializeLaunchDarkly(user, currentBuyer.id);

                if (settings.launchDarklyId && this.ldClient !== undefined) {
                    this._ldClientOnReady = () => {
                        this._ldClientOnChange = () => {
                            //todo in a separte story: we don't have to update all feature flags, [change] event has a parameter "settings" which will tell which flags have been chanaged.
                            this.featureFlags = this.ldClient.allFlags();
                            this._featureFlagSubject.next(this.featureFlags);
                            this._featureFlagsInitializedSubject.next(true);
                        };
                        this.ldClient.on('change', this._ldClientOnChange);
                        this.featureFlags = this.ldClient.allFlags();
                        this._featureFlagSubject.next(this.featureFlags);
                        this._featureFlagsInitializedSubject.next(true);
                    };
                    this.ldClient.on('ready', this._ldClientOnReady);
                } else {
                    // This is for when launch darkly cannot initialize.
                    // Call BE if launch darkly cannot initialize.
                    this.featureFlags = await this.getFeatureFlagsBE();
                    this._featureFlagSubject.next(this.featureFlags);
                    this._featureFlagsInitializedSubject.next(true);
                    //todo: feature flags may be stopped to be synced for realtime, which was a feature from LD SDK.  we do not have streaming or push mechanism for LSQ APIs yet.
                    // Should be resolved by a separate story.
                }
            });

        this._userService.logout$.subscribe(() => {
            this._featureFlagsInitializedSubject.next(false);
        });
    }

    private async getFeatureFlagsBE(): Promise<FeatureFlags> {
        try {
            const flags = (
                await this._getLaunchDarklyFeatureFlagsService.executeOnce(
                    new LaunchDarklyFeatureFlagsRequest({})
                )
            ).featureFlags;
            return flags;
        } catch (error) {
            this._errorHandler.handleError(
                // If error handler is configured this should not block the browser.
                new Error(`Failed to get Flags from LSQ backend API, please check logs.`)
            );
            return null;
        }
    }

    // this should be deprecated, no references to this method? need to check.
    public getFeatureFlags(): void {
        if (this.ldClient) {
            this.featureFlags = this.ldClient.allFlags();
            this._featureFlagSubject.next(this.featureFlags);
        }
    }

    public isOn(featureFlag: string): Observable<boolean> {
        return this._featureFlagSubject.pipe(map((flags) => this._flagIsOn(flags, featureFlag)));
    }

    public isOnSync(featureFlag: string): boolean {
        return this._flagIsOn(this.featureFlags, featureFlag);
    }

    public flagData(featureFlag: string): Observable<boolean | string | number> {
        return this._featureFlagSubject.pipe(
            map((flags) => (flags[featureFlag] ? flags[featureFlag] : false))
        );
    }

    public initializeLaunchDarkly(user: User, buyerKey: string): LaunchDarkly.LDClient | undefined {
        const launchDarklyHash =
            user &&
            user.alternateUserKeys &&
            user.alternateUserKeys.length &&
            (user.alternateUserKeys.find(
                (key) => key.alternateUserKeyType === AlternateUserKeyType.LaunchDarklyImpersonated
            ) ||
                user.alternateUserKeys.find(
                    (key) => key.alternateUserKeyType === AlternateUserKeyType.LaunchDarkly
                ));
        if (user && user.alternateUserKeys && !!user.alternateUserKeys.length) {
            return LaunchDarkly.initialize(
                this.settings.launchDarklyId,
                {
                    key: `${user.impersonationKey || user.userKey}`,
                    custom: {
                        companyKey: user.currentCompany ? `${user.currentCompany.companyKey}` : '',
                        companyName: user.currentCompany ? `${user.currentCompany.name}` : '',
                        buyerKey: buyerKey ?? ''
                    }
                },
                {
                    // Get first Launch Darkly key and set to hash
                    hash: launchDarklyHash.key
                }
            );
        } else {
            console.warn('Launch Darkly Key not found!');
        }

        return undefined;
    }

    private _flagIsOn(flags: FeatureFlags, featureFlag: string): boolean {
        if (this.settings.launchDarklyId) {
            if (flags) {
                return !!flags[featureFlag];
            }
            this._errorHandler.handleError(
                // If error handler is configured this should not block the browser.
                new Error(
                    `Flags are not populated, this may be due to a flag provider being down. User was trying to retrieve ${featureFlag} flag`
                )
            );
            return false;
        } else {
            return true;
        }
    }

    private async _getCurrentBuyer$(user: User): Promise<CurrentBuyer> {
        if (this._claimService.isOnSync(DashboardClaim.FinancingAccountPortal)) {
            return this._listAllBusinessUnitNamesAndIdsService
                .executeOnce(new ListAllBusinessUnitNamesAndIdsRequest())
                .then(({ businessUnitNames }) => ({
                    id: businessUnitNames[0].buyerKey,
                    companyName: businessUnitNames[0].name
                }));
        } else if (this._claimService.isOnSync(DashboardClaim.FinancingBuyerPortal)) {
            return {
                id: user?.currentCompany.alternateCompanyKey,
                companyName: user?.currentCompany.name
            };
        } else if (this._claimService.isOnSync(DashboardClaim.FinancingVendorPortal)) {
            return (
                this._listBuyersService
                    .executeOnce(new ListBuyersRequest())
                    // NOTE: Here we are assuming that a seller only ever has 1 buyer.
                    // In a true multi-buyer scenario, we would need a more sophisticated "get my buyer" API.
                    .then(({ rows }) => ({
                        id: rows[0].buyerKey,
                        companyName: rows[0].buyerCompanyName
                    }))
            );
        } else {
            return {};
        }
    }
}
